import type { FunctionComponent } from 'react';

import {
  ReplotChartNames,
  BarChart as ReplotsBarChart,
  BoxPlot as ReplotsBoxPlot,
  GroupedBarChart as ReplotsGroupedBarChartHorizontal,
  KdeChart as ReplotsKDE,
  LineChart as ReplotsLineChart,
  SankeyChart as ReplotsSankeyChart,
  VolumeChart as ReplotsVolumeChart,
} from '@revelio/replots';

import BarChartHorizontal from './lib/bar/BarChartHorizontal';
import BarChartHorizontalMirror from './lib/bar/BarChartHorizontalMirror';
import BarChartVertical from './lib/bar/BarChartVertical';
import GroupedBarChartHorizontal from './lib/bar/GroupedBarChartHorizontal';
import StackedBarChartHorizontal from './lib/bar/StackedBarChartHorizontal';
import Histogram from './lib/histogram/Histogram';
import KDEPlot from './lib/kde/KDEPlot';
import {
  DailyLineChart,
  type DailyLineChartProps,
} from './lib/line/daily-line-chart';
import LineChart from './lib/line/LineChart';
import {
  MonthlyLineChart,
  type MonthlyLineChartProps,
} from './lib/line/monthly-line-chart';
import Meter from './lib/meter/Meter';
import { D3NoMatch } from './lib/no-match';
import { OrgChart, OrgFromParentPositions } from './lib/org';
// png download imports
import {
  BarChartHorizontalGenerator,
  GroupedBarChartHorizontalGenerator,
  LineChartGenerator,
  MainPostingsPlotGenerator,
  SankeyGenerator,
  StackedBarChartHorizontalGenerator,
} from './lib/plot-generators';
import { BarChartHorizontalMirrorGenerator } from './lib/plot-generators/BarChartHorizontalMirrorGenerator';
import { BarChartVerticalGenerator } from './lib/plot-generators/BarChartVerticalGenerator';
import { HistogramGenerator } from './lib/plot-generators/HistogramGenerator';
import { KDEPlotGenerator } from './lib/plot-generators/kde/KDEPlotGenerator';
import { MeterGenerator } from './lib/plot-generators/meter/MeterGenerator';
import MainPostingsPlot from './lib/postings/MainPostingsPlot';
import SankeyDiagram from './lib/sankey/SankeyDiagram';
import ScatterPlot from './lib/scatter/ScatterPlot';
import Stats from './lib/stats/Stats';
import TreeDiagram from './lib/tree/TreeDiagram';
import { pngDownloader } from './lib/utilities/pngDownloader';
import {
  type D3ChartGenerator,
  D3ChartNames,
  type IBarChartHorizontal,
  type IBarChartHorizontalMirror,
  type IBarChartVertical,
  type IBaseD3Chart,
  type IBoxPlot,
  type IGroupedBarChartHorizontal,
  type IHistogram,
  type IKDEPlot,
  type ILineChart,
  type IMainPostingsPlot,
  type IMeter,
  type IOrgChart,
  type IOrgFromParentPositions,
  type ISankeyDiagram,
  type IScatterPlot,
  type IStackedBarChartHorizontal,
  type ITreePlot,
} from './model';

export * from './lib/line/line-chart';
export * from './lib/plot-generators';
export * from './lib/tooltip/tooltip';
export * from './lib/utilities/colors';
export * from './lib/utilities/plot-colors';
export * from './model';

export {
  KDEPlot,
  //Todo: remove this when sentiment is switched to GQL
  ScatterPlot,
  TreeDiagram,
};

export const D3ChartComponentLookup = {
  /* Newer Reploted Chart Components */
  [ReplotChartNames.BarChart]: ReplotsBarChart,
  [ReplotChartNames.LineChart]: ReplotsLineChart,
  [ReplotChartNames.VolumeChart]: ReplotsVolumeChart,
  [ReplotChartNames.KDE]: ReplotsKDE,
  [ReplotChartNames.Sankey]: ReplotsSankeyChart,
  [ReplotChartNames.BoxPlot]: ReplotsBoxPlot,
  /* Older D3 Chart Components */
  [D3ChartNames.BoxPlot]: ReplotsBoxPlot as FunctionComponent<IBoxPlot>,
  [D3ChartNames.BarChartHorizontal]:
    BarChartHorizontal as FunctionComponent<IBarChartHorizontal>,
  [D3ChartNames.BarChartHorizontalMirror]:
    BarChartHorizontalMirror as FunctionComponent<IBarChartHorizontalMirror>,
  [D3ChartNames.BarChartVertical]:
    BarChartVertical as FunctionComponent<IBarChartVertical>,
  [D3ChartNames.StackedBarChartHorizontal]:
    StackedBarChartHorizontal as FunctionComponent<IStackedBarChartHorizontal>,
  [D3ChartNames.GroupedBarChartHorizontal]:
    GroupedBarChartHorizontal as FunctionComponent<IGroupedBarChartHorizontal>,
  [D3ChartNames.Histogram]: Histogram as FunctionComponent<IHistogram>,
  [D3ChartNames.LineChart]: LineChart as FunctionComponent<ILineChart>,
  [D3ChartNames.MainPostingsPlot]:
    MainPostingsPlot as FunctionComponent<IMainPostingsPlot>,
  [D3ChartNames.SankeyDiagram]:
    SankeyDiagram as FunctionComponent<ISankeyDiagram>,
  [D3ChartNames.Stats]: Stats as FunctionComponent<IBaseD3Chart>,
  [D3ChartNames.ScatterPlot]: ScatterPlot as FunctionComponent<IScatterPlot>,
  [D3ChartNames.TreeDiagram]: TreeDiagram as FunctionComponent<ITreePlot>,
  [D3ChartNames.KDE]: KDEPlot as FunctionComponent<IKDEPlot>,
  [D3ChartNames.Meter]: Meter as FunctionComponent<IMeter>,
  [D3ChartNames.OrgFromParentPositions]:
    OrgFromParentPositions as FunctionComponent<IOrgFromParentPositions>,
  [D3ChartNames.OrgChart]: OrgChart as FunctionComponent<IOrgChart>,
  D3NoMatch: D3NoMatch,
};

export const D3ChartGeneratorLookup: {
  [key in D3ChartNames]?: D3ChartGenerator | any | undefined;
} = {
  [D3ChartNames.BarChartHorizontal]: BarChartHorizontalGenerator,
  [D3ChartNames.BarChartHorizontalMirror]: BarChartHorizontalMirrorGenerator,
  [D3ChartNames.BarChartVertical]: BarChartVerticalGenerator,
  [D3ChartNames.GroupedBarChartHorizontal]: GroupedBarChartHorizontalGenerator,
  [D3ChartNames.Histogram]: HistogramGenerator,
  [D3ChartNames.LineChart]: LineChartGenerator,
  [D3ChartNames.MainPostingsPlot]: MainPostingsPlotGenerator,
  [D3ChartNames.SankeyDiagram]: SankeyGenerator,
  [D3ChartNames.StackedBarChartHorizontal]: StackedBarChartHorizontalGenerator,
  [D3ChartNames.KDE]: KDEPlotGenerator,
  [D3ChartNames.Meter]: MeterGenerator,
  [D3ChartNames.D3NoMatch]: undefined,
};
